import * as React from "react";
import clsx from "clsx";
import { Check, X } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Modal } from "../../../ui/components/Modal";
import { Callout } from "../../../ui/components/Callout";
import { Switch } from "../../../ui/components/Switch";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import {
  fetchSmartLinkClaimComparison,
  setSmartLinkClaimEnabled,
  syncSmartLinkClaimFromPro,
  syncSmartLinkClaimFromSmart,
} from "../../lib/api/smart-link";
import type { SmartLinkClaim, SmartLinkComparison, SmartLinkCompareField } from "../../lib/api/smart-link";
import { canonicalPermissionName } from "../../lib/permission-name-canonical";
import { parsePermissionParts, formatResourceLabel, formatRoleLabel } from "../../lib/permission-groups";
import * as s from "./SmartLinkClaimCompareModal.css";

export interface SmartLinkClaimCompareModalProps {
  claim: SmartLinkClaim;
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Called after a successful "Sync from Pro/Smart" — the claim's syncStatus badge in the underlying list is stale until the list refetches, since this modal only reloads its own comparison state. */
  onSynced?: () => void;
}

function formatValue(t: (key: string) => string, value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "boolean") {
    return value ? t("pro.system.smartLink.compare.yes") : t("pro.system.smartLink.compare.no");
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? [...value].sort().join(", ") : "—";
  }
  return String(value);
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

const PROFILE_FIELDS: SmartLinkCompareField["field"][] = [
  "email",
  "firstName",
  "lastName",
  "phone",
  "phoneVerified",
  "emailVerified",
];

const FIELD_LABEL_KEYS: Record<SmartLinkCompareField["field"], string> = {
  email: "pro.system.smartLink.compare.field.email",
  firstName: "pro.system.smartLink.compare.field.firstName",
  lastName: "pro.system.smartLink.compare.field.lastName",
  phone: "pro.system.smartLink.compare.field.phone",
  phoneVerified: "pro.system.smartLink.compare.field.phoneVerified",
  emailVerified: "pro.system.smartLink.compare.field.emailVerified",
  globalRoles: "pro.system.smartLink.compare.field.globalRoles",
  globalPermissions: "pro.system.smartLink.compare.field.globalPermissions",
};

type RoleRow = {
  role: string;
  label: string;
  inPro: boolean;
  inSmart: boolean;
  matches: boolean;
};

function buildRoleRows(proRoles: string[], smartRoles: string[]): RoleRow[] {
  const proSet = new Set(proRoles);
  const smartSet = new Set(smartRoles);
  const all = new Set([...proRoles, ...smartRoles]);
  return [...all]
    .sort((a, b) => a.localeCompare(b))
    .map((role) => {
      const inPro = proSet.has(role);
      const inSmart = smartSet.has(role);
      return { role, label: formatRoleLabel(role), inPro, inSmart, matches: inPro === inSmart };
    });
}

/** Common actions first, in CRUD order; anything else follows alphabetically. */
const ACTION_ORDER = ["create", "read", "update", "delete"];

function orderActions(actions: Set<string>): string[] {
  const primary = ACTION_ORDER.filter((a) => actions.has(a));
  const rest = [...actions].filter((a) => !ACTION_ORDER.includes(a)).sort((a, b) => a.localeCompare(b));
  return [...primary, ...rest];
}

/** First letter of each action; extends to more letters only if two actions collide. */
function actionInitials(actions: string[]): Map<string, string> {
  const used = new Set<string>();
  const initials = new Map<string, string>();
  for (const action of actions) {
    let len = 1;
    let initial = action.slice(0, len).toUpperCase();
    while (used.has(initial) && len < action.length) {
      len += 1;
      initial = action.slice(0, len).toUpperCase();
    }
    used.add(initial);
    initials.set(action, initial);
  }
  return initials;
}

type PermissionResourceRow = {
  resource: string;
  label: string;
  matches: boolean;
  actions: Array<{ action: string; initial: string }>;
  proActions: Set<string>;
  smartActions: Set<string>;
};

function buildPermissionResourceRows(proPerms: string[], smartPerms: string[]): PermissionResourceRow[] {
  const proByResource = new Map<string, Set<string>>();
  const smartByResource = new Map<string, Set<string>>();
  const allResources = new Set<string>();

  const collect = (perms: string[], byResource: Map<string, Set<string>>) => {
    for (const raw of perms) {
      const { resource, action } = parsePermissionParts(canonicalPermissionName(raw));
      allResources.add(resource);
      const set = byResource.get(resource) ?? new Set<string>();
      set.add(action);
      byResource.set(resource, set);
    }
  };
  collect(proPerms, proByResource);
  collect(smartPerms, smartByResource);

  return [...allResources]
    .sort((a, b) => a.localeCompare(b))
    .map((resource) => {
      const proActions = proByResource.get(resource) ?? new Set<string>();
      const smartActions = smartByResource.get(resource) ?? new Set<string>();
      const ordered = orderActions(new Set([...proActions, ...smartActions]));
      const initials = actionInitials(ordered);
      const matches =
        proActions.size === smartActions.size && [...proActions].every((a) => smartActions.has(a));
      return {
        resource,
        label: formatResourceLabel(resource),
        matches,
        actions: ordered.map((action) => ({ action, initial: initials.get(action) ?? "" })),
        proActions,
        smartActions,
      };
    });
}

/** Center status icon: shows whether the two sides agree. Reserved for match/mismatch — see presence dots below for a deliberately different signal. */
function MatchIcon({ matches, t }: { matches: boolean; t: (key: string) => string }) {
  return (
    <span
      className={clsx(s.matchIcon, matches ? s.matchIconMatch : s.matchIconMismatch)}
      role="img"
      aria-label={t(matches ? "pro.system.smartLink.compare.matches" : "pro.system.smartLink.compare.mismatch")}
    >
      {matches ? <Check size={16} strokeWidth={2.5} /> : <X size={16} strokeWidth={2.5} />}
    </span>
  );
}

function LetterChips({
  actions,
  granted,
}: {
  actions: Array<{ action: string; initial: string }>;
  granted: Set<string>;
}) {
  return (
    <div className={s.lettersLine}>
      {actions.map((a) => (
        <span
          key={a.action}
          className={clsx(s.letterChip, granted.has(a.action) ? s.letterChipActive : s.letterChipMuted)}
          title={a.action}
        >
          {a.initial}
        </span>
      ))}
    </div>
  );
}

export function SmartLinkClaimCompareModal({ claim, open, setOpen, onSynced }: SmartLinkClaimCompareModalProps) {
  const { t } = useI18n();
  const { accessToken } = useAuth();
  const token = accessToken ?? (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [comparison, setComparison] = React.useState<SmartLinkComparison | null>(null);
  const [syncingFromPro, setSyncingFromPro] = React.useState(false);
  const [syncingFromSmart, setSyncingFromSmart] = React.useState(false);
  const [syncError, setSyncError] = React.useState<string | null>(null);

  // Settings section: local mirror of claim.linkEnabled, kept in sync with
  // the prop (reset whenever a different claim is opened) but updated
  // optimistically the moment the toggle succeeds.
  const [linkEnabled, setLinkEnabled] = React.useState(claim.linkEnabled);
  const [togglingLink, setTogglingLink] = React.useState(false);
  const [toggleError, setToggleError] = React.useState<string | null>(null);
  React.useEffect(() => {
    setLinkEnabled(claim.linkEnabled);
  }, [claim.id, claim.linkEnabled]);

  const loadComparison = React.useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    return fetchSmartLinkClaimComparison(token, claim.id)
      .then((result) => {
        setComparison(result);
        // The compare endpoint itself persists a fresh syncStatus as a side
        // effect (not just the two sync actions below) -- so the list this
        // modal was opened from can go stale just from viewing a claim,
        // not only from clicking a sync button. Let the caller refetch.
        onSynced?.();
      })
      .catch((err) => setError(err?.message || t("pro.system.smartLink.compare.error.load")))
      .finally(() => setLoading(false));
  }, [token, claim.id, t, onSynced]);

  React.useEffect(() => {
    // A disabled link's compare section stays inert -- no live fetch, no
    // sync buttons -- since acting on it wouldn't do anything meaningful.
    if (!open || !linkEnabled) return;
    loadComparison();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, claim.id, linkEnabled]);

  const handleToggleLink = async (nextEnabled: boolean) => {
    if (!token || togglingLink) return;
    setTogglingLink(true);
    setToggleError(null);
    try {
      const result = await setSmartLinkClaimEnabled(token, claim.id, nextEnabled);
      setLinkEnabled(result.linkEnabled);
      onSynced?.();
    } catch (err: any) {
      setToggleError(err?.message || t("pro.system.smartLink.manage.toggleError"));
    } finally {
      setTogglingLink(false);
    }
  };

  const handleSyncFromPro = async () => {
    if (!token || syncingFromPro) return;
    setSyncingFromPro(true);
    setSyncError(null);
    try {
      await syncSmartLinkClaimFromPro(token, claim.id);
      await loadComparison();
    } catch (err: any) {
      setSyncError(err?.message || t("pro.system.smartLink.compare.syncFromProError"));
    } finally {
      setSyncingFromPro(false);
    }
  };

  const handleSyncFromSmart = async () => {
    if (!token || syncingFromSmart) return;
    setSyncingFromSmart(true);
    setSyncError(null);
    try {
      await syncSmartLinkClaimFromSmart(token, claim.id);
      await loadComparison();
    } catch (err: any) {
      setSyncError(err?.message || t("pro.system.smartLink.compare.syncFromSmartError"));
    } finally {
      setSyncingFromSmart(false);
    }
  };

  const fieldsByKey = React.useMemo(() => {
    const map = new Map<SmartLinkCompareField["field"], SmartLinkCompareField>();
    comparison?.fields.forEach((f) => map.set(f.field, f));
    return map;
  }, [comparison]);

  const profileFields = PROFILE_FIELDS.map((field) => fieldsByKey.get(field)).filter(
    (f): f is SmartLinkCompareField => Boolean(f),
  );

  const rolesField = fieldsByKey.get("globalRoles");
  const roleRows = rolesField
    ? buildRoleRows(asStringArray(rolesField.proValue), asStringArray(rolesField.smartValue))
    : [];

  const permissionsField = fieldsByKey.get("globalPermissions");
  const permissionRows = permissionsField
    ? buildPermissionResourceRows(
        asStringArray(permissionsField.proValue),
        asStringArray(permissionsField.smartValue),
      )
    : [];

  const allSynced = comparison ? comparison.fields.every((f) => f.matches) : null;

  // Single header status slot -- "not linked" takes priority over a
  // synced/out-of-sync verdict, since comparing an unlinked claim's mostly-
  // null Pro side against Smart's live values isn't a meaningful "out of
  // sync" signal (see also: sync buttons hidden below for the same reason).
  const headerStatus =
    linkEnabled && comparison
      ? !comparison.linked
        ? { variant: "info" as const, icon: undefined, label: t("pro.system.smartLink.compare.notLinked") }
        : allSynced != null
          ? {
              variant: allSynced ? ("success" as const) : ("error" as const),
              icon: allSynced ? <Check size={14} /> : <X size={14} />,
              label: t(
                allSynced
                  ? "pro.system.smartLink.compare.status.synced"
                  : "pro.system.smartLink.compare.status.outOfSync",
              ),
            }
          : null
      : null;

  return (
    <Modal open={open} setOpen={setOpen} minWidth="480px" maxWidth="640px" maxHeight="70vh" closeOnClickOff>
      <Modal.Header>
        <Text level="h2" weight="bold" className={s.title}>
          {t("pro.system.smartLink.manage.title")}
        </Text>
        {headerStatus && (
          <div className={s.statusCallout}>
            <Callout variant={headerStatus.variant} centered icon={headerStatus.icon}>
              {headerStatus.label}
            </Callout>
          </div>
        )}
      </Modal.Header>

      <Modal.Body>
        <div className={s.bodyStack}>
          <div className={s.section}>
            <h3 className={s.sectionTitle}>{t("pro.system.smartLink.manage.settingsSection")}</h3>
            <Switch
              label={t("pro.system.smartLink.manage.syncEnabled")}
              checked={linkEnabled}
              disabled={togglingLink}
              onCheckedChange={(v) => void handleToggleLink(v)}
            />
            <p className={s.linkedStatusText}>
              {claim.linkedUserId
                ? t("pro.system.smartLink.manage.linkedTo", {
                    email: claim.linkedUser?.email ?? claim.linkedUserId,
                  })
                : t("pro.system.smartLink.manage.notYetLinked")}
            </p>
            {toggleError && (
              <div className={s.banner}>
                <Callout variant="error">{toggleError}</Callout>
              </div>
            )}
          </div>

          <div className={s.section}>
            <h3 className={s.sectionTitle}>{t("pro.system.smartLink.compare.title")}</h3>

            {!linkEnabled && (
              <div className={s.banner}>
                <Callout variant="info">{t("pro.system.smartLink.manage.compareDisabled")}</Callout>
              </div>
            )}

            {linkEnabled && loading && <Text level="body">{t("pro.system.smartLink.compare.loading")}</Text>}

            {linkEnabled && error && (
              <div className={s.banner}>
                <Callout variant="error">{error}</Callout>
              </div>
            )}

            {linkEnabled && syncError && (
              <div className={s.banner}>
                <Callout variant="error">{syncError}</Callout>
              </div>
            )}

            {linkEnabled && comparison && (
              <div className={s.sections}>
                {comparison.smartError && (
                  <div className={s.banner}>
                    <Callout variant="error">
                      {t("pro.system.smartLink.compare.smartUnavailable")} ({comparison.smartError})
                    </Callout>
                  </div>
                )}

                {profileFields.length > 0 && (
                  <div className={s.section}>
                    <h3 className={s.sectionTitle}>{t("pro.system.smartLink.compare.section.profile")}</h3>
                    <div className={s.columnHeaders}>
                      <p className={s.columnHeaderLabel}>{t("pro.system.smartLink.compare.col.lionEnergy")}</p>
                      <span />
                      <p className={s.columnHeaderLabel}>{t("pro.system.smartLink.compare.col.lionSmart")}</p>
                    </div>
                    <div className={s.rowList}>
                      {profileFields.map((f) => (
                        <div key={f.field}>
                          <p className={s.fieldLabel}>{t(FIELD_LABEL_KEYS[f.field])}</p>
                          <div className={s.compareGrid}>
                            <div className={clsx(s.valueCard, !f.matches && s.valueCardMismatch)}>
                              <p className={s.valueText}>{formatValue(t, f.proValue)}</p>
                            </div>
                            <MatchIcon matches={f.matches} t={t} />
                            <div className={clsx(s.valueCard, !f.matches && s.valueCardMismatch)}>
                              <p className={s.valueText}>{formatValue(t, f.smartValue)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {roleRows.length > 0 && (
                  <div className={s.section}>
                    <h3 className={s.sectionTitle}>{t("pro.system.smartLink.compare.section.roles")}</h3>
                    <div className={s.columnHeaders}>
                      <p className={s.columnHeaderLabel}>{t("pro.system.smartLink.compare.col.lionEnergy")}</p>
                      <span />
                      <p className={s.columnHeaderLabel}>{t("pro.system.smartLink.compare.col.lionSmart")}</p>
                    </div>
                    <div className={s.rowList}>
                      {roleRows.map((row) => (
                        <div key={row.role}>
                          <p className={s.fieldLabel}>{row.label}</p>
                          <div className={s.compareGrid}>
                            <div className={clsx(s.valueCard, !row.matches && s.valueCardMismatch)}>
                              <p className={s.valueText}>{formatValue(t, row.inPro)}</p>
                            </div>
                            <MatchIcon matches={row.matches} t={t} />
                            <div className={clsx(s.valueCard, !row.matches && s.valueCardMismatch)}>
                              <p className={s.valueText}>{formatValue(t, row.inSmart)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {permissionRows.length > 0 && (
                  <div className={s.section}>
                    <h3 className={s.sectionTitle}>{t("pro.system.smartLink.compare.section.permissions")}</h3>
                    <div className={s.columnHeaders}>
                      <p className={s.columnHeaderLabel}>{t("pro.system.smartLink.compare.col.lionEnergy")}</p>
                      <span />
                      <p className={s.columnHeaderLabel}>{t("pro.system.smartLink.compare.col.lionSmart")}</p>
                    </div>
                    <div className={s.rowList}>
                      {permissionRows.map((row) => (
                        <div key={row.resource}>
                          <p className={s.fieldLabel}>{row.label}</p>
                          <div className={s.compareGrid}>
                            <div className={clsx(s.valueCard, !row.matches && s.valueCardMismatch)}>
                              <LetterChips actions={row.actions} granted={row.proActions} />
                            </div>
                            <MatchIcon matches={row.matches} t={t} />
                            <div className={clsx(s.valueCard, !row.matches && s.valueCardMismatch)}>
                              <LetterChips actions={row.actions} granted={row.smartActions} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        {linkEnabled && comparison?.linked && allSynced === false && (
          <>
            <Button
              type="button"
              color="neutral"
              variant="outline"
              loading={syncingFromPro}
              disabled={syncingFromPro}
              onClick={() => void handleSyncFromPro()}
            >
              {t("pro.system.smartLink.compare.syncFromPro")}
            </Button>
            <Button
              type="button"
              color="neutral"
              variant="outline"
              loading={syncingFromSmart}
              disabled={syncingFromSmart}
              onClick={() => void handleSyncFromSmart()}
            >
              {t("pro.system.smartLink.compare.syncFromSmart")}
            </Button>
          </>
        )}
        <Button type="button" color="primary" onClick={() => setOpen(false)}>
          {t("shared.common.close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
